/**
 * Party-generic tabs shared by customers and suppliers: Files (documents
 * attached to the party record) and Activity (this party's own audit trail).
 *
 * Both are backed by APIs that already exist — `/attachments?entityType=party`
 * and `/audit-logs?entityId=<party>` — so neither is an empty shell. Each is
 * only rendered by PartyDetailPage when the user holds the read permission.
 */
import { Download, FileText, History, Loader2, Trash2, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Skeleton } from '../../components/shadcn/index.js';
import { Button, Card, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import {
  type Attachment,
  ATTACHMENT_ALLOWED_MIME,
  ATTACHMENT_MAX_BYTES,
  deleteAttachment,
  fetchAttachmentObjectUrl,
  listAttachments,
  uploadAttachment,
} from '../../lib/attachments.js';
import { cn } from '../../lib/cn.js';
import { formatDate, formatDateTime } from '../../lib/intl.js';
import { AttachmentThumb, formatBytes } from '../common/FileUploadDialog.js';

/** entityType used for files hung off a customer/supplier record. */
export const PARTY_ENTITY_TYPE = 'party';

// ----------------------------------------------------------------- Files tab

/** Contracts, price lists, trade licences — anything filed against the party. */
export function PartyFilesTab({ partyId }: { partyId: string }): JSX.Element {
  const { t } = useTranslation('parties');
  const { t: tc, i18n } = useTranslation();
  const canWrite = usePermission('attachments.write');
  const canDelete = usePermission('attachments.delete');
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Attachment[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setItems(await listAttachments(PARTY_ENTITY_TYPE, partyId));
    } catch {
      setItems([]);
    }
  }, [partyId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const validate = useCallback((file: File): string | null => {
    if (!file.type || !ATTACHMENT_ALLOWED_MIME.has(file.type)) {
      return tc('fileUpload.errors.mime', { defaultValue: 'That file type is not allowed.' });
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      return tc('fileUpload.errors.size', { defaultValue: 'Each file must be 25 MB or smaller.' });
    }
    return null;
  }, [tc]);

  const uploadFiles = useCallback(async (files: FileList | File[]): Promise<void> => {
    setError(null);
    const arr = Array.from(files);
    const firstBad = arr.map((f) => ({ f, e: validate(f) })).find((x) => x.e);
    if (firstBad) setError(`${firstBad.f.name}: ${firstBad.e}`);
    const valid = arr.filter((f) => !validate(f));
    if (valid.length === 0) return;
    setBusy(true);
    try {
      for (const f of valid) await uploadAttachment(PARTY_ENTITY_TYPE, partyId, f);
      await refresh();
    } catch {
      setError(tc('fileUpload.errors.generic', { defaultValue: 'Upload failed. Try again.' }));
    } finally {
      setBusy(false);
    }
  }, [partyId, refresh, validate, tc]);

  const onRemove = async (id: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await deleteAttachment(id);
      await refresh();
    } catch {
      setError(tc('fileUpload.errors.generic', { defaultValue: 'Upload failed. Try again.' }));
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async (a: Attachment): Promise<void> => {
    const url = await fetchAttachmentObjectUrl(a.id);
    const link = document.createElement('a');
    link.href = url;
    link.download = a.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  return (
    <div className="flex flex-col gap-4">
      {canWrite && (
        <div
          role="button"
          tabIndex={0}
          aria-label={tc('fileUpload.pick', { defaultValue: 'Choose files' })}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-7 text-center transition',
            dragOver ? 'border-brand bg-brand/5' : 'border-border bg-bg-subtle hover:border-brand/60',
          )}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files?.length) void uploadFiles(e.dataTransfer.files);
          }}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={[...ATTACHMENT_ALLOWED_MIME].join(',')}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) void uploadFiles(e.target.files);
              if (e.target) e.target.value = '';
            }}
          />
          {busy
            ? <Loader2 className="h-6 w-6 animate-spin text-brand" aria-hidden />
            : <Upload className="h-6 w-6 text-fg-muted" aria-hidden />}
          <div className="text-sm font-medium text-fg">
            {busy
              ? tc('fileUpload.uploading', { defaultValue: 'Uploading…' })
              : tc('fileUpload.dropHint', { defaultValue: 'Drop files here or click to browse' })}
          </div>
          <div className="text-xs text-fg-muted">
            {t('files.hint', { defaultValue: 'Contracts, price lists, licences — anything about this party.' })}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-danger" role="alert">{error}</p>}

      {items === null ? (
        <Card className="p-4"><Skeleton className="h-24 w-full" /></Card>
      ) : items.length === 0 ? (
        <Card className="p-6">
          <EmptyState
            icon={<FileText className="h-8 w-8" aria-hidden />}
            title={t('files.empty', { defaultValue: 'No files yet.' })}
            description={canWrite
              ? t('files.emptyHint', { defaultValue: 'Drop a file above to keep it with this record.' })
              : undefined}
          />
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-border">
            {items.map((a) => (
              <li key={a.id} className="flex items-center gap-3 px-4 py-2.5">
                <AttachmentThumb attachment={a} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-fg">{a.filename}</div>
                  <div className="text-xs text-fg-muted">
                    {formatBytes(a.sizeBytes)} · {formatDate(a.uploadedAt, { locale: i18n.language })}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void onDownload(a)}
                  aria-label={tc('fileUpload.download', { defaultValue: 'Download' })}
                  title={tc('fileUpload.download', { defaultValue: 'Download' })}
                >
                  <Download className="h-4 w-4" aria-hidden />
                </Button>
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => void onRemove(a.id)}
                    aria-label={tc('fileUpload.remove', { defaultValue: 'Remove' })}
                    title={tc('fileUpload.remove', { defaultValue: 'Remove' })}
                  >
                    <Trash2 className="h-4 w-4 text-danger" aria-hidden />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

// -------------------------------------------------------------- Activity tab

interface AuditRow {
  id: string;
  actorName: string | null;
  action: string;
  operation: string | null;
  entityType: string;
  createdAt: string;
}

const OPERATION_TONE: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'danger',
};

/**
 * This party's own audit trail — who changed the record and when — read from
 * the audit engine with the `entityId` filter added alongside this page.
 * Rendered only when the tenant has the audit module on and the user holds
 * `audit_logs.read`; a 403/disabled module degrades to the empty state.
 */
export function PartyActivityTab({ partyId }: { partyId: string }): JSX.Element {
  const { t } = useTranslation('parties');
  const { i18n } = useTranslation();
  const [rows, setRows] = useState<AuditRow[] | null>(null);

  useEffect(() => {
    let active = true;
    setRows(null);
    void api()
      .get<{ items: AuditRow[] }>('/audit-logs', { params: { entityId: partyId, limit: 100 } })
      .then((r) => { if (active) setRows(r.data.items); })
      .catch(() => { if (active) setRows([]); });
    return () => { active = false; };
  }, [partyId]);

  if (rows === null) return <Card className="p-4"><Skeleton className="h-40 w-full" /></Card>;

  if (rows.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState
          icon={<History className="h-8 w-8" aria-hidden />}
          title={t('activity.empty', { defaultValue: 'No recorded activity yet.' })}
          description={t('activity.emptyHint', { defaultValue: 'Edits to this record will appear here.' })}
        />
      </Card>
    );
  }

  return (
    <Card className="p-0">
      <ul className="divide-y divide-border">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
            {r.operation && (
              <Badge variant={OPERATION_TONE[r.operation] ?? 'secondary'}>
                {t(`activity.operation.${r.operation}`, { defaultValue: r.operation })}
              </Badge>
            )}
            <span className="min-w-0 flex-1 truncate text-sm text-fg">{r.action}</span>
            <span className="text-xs text-fg-muted">
              {r.actorName ?? t('activity.system', { defaultValue: 'System' })}
            </span>
            <span className="whitespace-nowrap text-xs text-fg-subtle">
              {formatDateTime(r.createdAt, { locale: i18n.language })}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
